import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Truck, Users, MapPin, Clock, ClockCountdown, Warning, ArrowUp, ArrowDown, X, WifiSlash, WifiHigh, WifiMedium, PaperPlaneTilt, CheckCircle, UserCircle, Package, Storefront, Hourglass } from '@phosphor-icons/react';

const STATUS_COLORS = {
  absent: { bg: 'bg-[#FF3333]/10', text: 'text-[#FF3333]', dot: 'bg-[#FF3333]' },
  checked_in: { bg: 'bg-[#00C853]/10', text: 'text-[#00C853]', dot: 'bg-[#00C853]' },
  driving: { bg: 'bg-[#002FA7]/10', text: 'text-[#002FA7]', dot: 'bg-[#002FA7]' },
  waiting_pickup: { bg: 'bg-[#FFD700]/10', text: 'text-[#B8860B]', dot: 'bg-[#FFD700]' },
  waiting_drop: { bg: 'bg-[#FFD700]/10', text: 'text-[#B8860B]', dot: 'bg-[#FFD700]' },
  waiting_trip: { bg: 'bg-[#808080]/10', text: 'text-[#808080]', dot: 'bg-[#808080]' },
  breakfast: { bg: 'bg-[#FF8C00]/10', text: 'text-[#FF8C00]', dot: 'bg-[#FF8C00]' },
  lunch: { bg: 'bg-[#FF8C00]/10', text: 'text-[#FF8C00]', dot: 'bg-[#FF8C00]' },
  permission: { bg: 'bg-[#FF8C00]/10', text: 'text-[#FF8C00]', dot: 'bg-[#FF8C00]' },
  mgmt_work: { bg: 'bg-[#808080]/10', text: 'text-[#808080]', dot: 'bg-[#808080]' },
  dinner: { bg: 'bg-[#FF8C00]/10', text: 'text-[#FF8C00]', dot: 'bg-[#FF8C00]' },
  nonproductive: { bg: 'bg-[#808080]/10', text: 'text-[#808080]', dot: 'bg-[#808080]' },
};

const STATUS_LABELS = {
  absent: 'Absent', checked_in: 'Checked In', driving: 'Driving',
  waiting_pickup: 'Waiting (Pickup)', waiting_drop: 'Waiting (Drop)',
  waiting_trip: 'Waiting for Trip', breakfast: 'Breakfast', lunch: 'Lunch',
  permission: 'Permission', mgmt_work: 'Mgmt Work', dinner: 'Dinner',
  nonproductive: 'Nonproductive'
};

const STATUS_BADGE = {
  pending: 'bg-[#808080]/10 text-[#808080]', allocated: 'bg-[#002FA7]/10 text-[#002FA7]',
  accepted: 'bg-[#00C853]/10 text-[#00C853]', in_progress: 'bg-[#002FA7]/10 text-[#002FA7]',
  pickup_reached: 'bg-[#FFD700]/10 text-[#B8860B]', pickup_done: 'bg-[#FF8C00]/10 text-[#FF8C00]',
  drop_reached: 'bg-[#FFD700]/10 text-[#B8860B]', completed: 'bg-[#00C853]/10 text-[#00C853]',
  cancelled: 'bg-[#FF3333]/10 text-[#FF3333]',
};

function fmtTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
}

function fmtAgo(seconds) {
  if (seconds == null) return 'Never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m ago`;
}

const HEALTH_META = {
  live:    { label: 'Live',    color: 'text-[#00C853]', bg: 'bg-[#00C853]/10', dot: 'bg-[#00C853]', Icon: WifiHigh },
  stale:   { label: 'Stale',   color: 'text-[#FF8C00]', bg: 'bg-[#FF8C00]/10', dot: 'bg-[#FF8C00]', Icon: WifiMedium },
  offline: { label: 'Offline', color: 'text-[#FF3333]', bg: 'bg-[#FF3333]/10', dot: 'bg-[#FF3333]', Icon: WifiSlash },
};

function QueueHealthCard() {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await api.get('/admin/queue-health');
        if (!cancelled) setData(res.data);
      } catch { /* ignore */ }
    };
    fetch();
    const t = setInterval(fetch, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const total = data?.total_queued || 0;
  const groups = data?.groups || [];
  const noSupply = groups.filter(g => g.queued > 0 && (g.available_pilots || 0) === 0);
  const tone = total === 0 ? 'ok' : noSupply.length > 0 ? 'breach' : 'warn';
  const headerClass = tone === 'ok'
    ? 'bg-[#00C853]/5 border-[#00C853]/20'
    : tone === 'warn'
    ? 'bg-[#FF8C00]/5 border-[#FF8C00]/20'
    : 'bg-[#FF3333]/5 border-[#FF3333]/20';
  const accent = tone === 'ok' ? 'text-[#00C853]' : tone === 'warn' ? 'text-[#FF8C00]' : 'text-[#FF3333]';

  return (
    <div className={`border ${headerClass} p-4 space-y-3`} data-testid="queue-health-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClockCountdown size={20} className={accent} weight="bold" />
          <h3 className={`text-sm font-bold uppercase tracking-widest ${accent}`}>Queue Health</h3>
        </div>
        {total > 0 && (
          <button
            onClick={() => setDialogOpen(true)}
            className="text-xs uppercase tracking-widest underline text-[#666] hover:text-[#0A0A0A]"
            data-testid="queue-health-view-all"
          >
            View all
          </button>
        )}
      </div>

      {total === 0 ? (
        <p className="text-sm text-[#666]">All trips allocated — no queue right now.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#666]">Total queued</p>
              <p className={`text-2xl font-black ${accent}`}>{total}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#666]">Vehicle types</p>
              <p className="text-2xl font-black text-[#0A0A0A]">{groups.length}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#666]">No supply</p>
              <p className={`text-2xl font-black ${noSupply.length ? 'text-[#FF3333]' : 'text-[#999]'}`}>{noSupply.length}</p>
            </div>
          </div>
          <div className="space-y-2">
            {groups.slice(0, 5).map(g => {
              const noAvail = (g.available_pilots || 0) === 0;
              return (
                <div
                  key={g.vehicle_type}
                  className={`flex items-center justify-between border p-2 ${noAvail ? 'border-[#FF3333]/20 bg-[#FF3333]/5' : 'border-[#E5E5E5]'}`}
                  data-testid={`queue-group-${g.vehicle_type}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{g.vehicle_type}</p>
                    <p className="text-[11px] text-[#666]">
                      {g.available_pilots} available · {g.in_use} in-use · oldest {g.max_wait_minutes}m
                    </p>
                  </div>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${noAvail ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#FF8C00]/10 text-[#FF8C00]'} font-bold`}>
                    {g.queued} queued
                  </span>
                </div>
              );
            })}
            {groups.length > 5 && (
              <p className="text-[11px] text-[#666] text-center">+{groups.length - 5} more — tap "View all"</p>
            )}
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Queue Health — All Vehicle Types</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            {groups.map(g => {
              const noAvail = (g.available_pilots || 0) === 0;
              return (
                <div key={g.vehicle_type} className="border border-[#E5E5E5]">
                  <div className={`flex items-center justify-between p-3 ${noAvail ? 'bg-[#FF3333]/5' : 'bg-[#F3F3F3]'}`}>
                    <div>
                      <p className="text-base font-bold">{g.vehicle_type}</p>
                      <p className="text-xs text-[#666]">{g.available_pilots} pilots available · {g.in_use} vehicles in use · oldest wait {g.max_wait_minutes}m</p>
                    </div>
                    <span className={`text-xs uppercase tracking-widest px-2 py-1 ${noAvail ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#FF8C00]/10 text-[#FF8C00]'} font-bold`}>{g.queued} queued</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white text-[10px] uppercase tracking-widest text-[#666]">
                        <th className="text-left p-2">Q#</th>
                        <th className="text-left p-2">Trip</th>
                        <th className="text-left p-2">Pickup</th>
                        <th className="text-left p-2">Fallback (Opt 2)</th>
                        <th className="text-right p-2">Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.trips.map(tr => (
                        <tr key={tr.id} className="border-t border-[#F3F3F3]">
                          <td className="p-2 font-bold text-[#FF8C00]">#{tr.queue_position}</td>
                          <td className="p-2 font-mono text-xs">{tr.id}</td>
                          <td className="p-2 truncate max-w-[150px]">{tr.pickup_supplier_name || '-'}</td>
                          <td className="p-2 text-[#666]">{tr.vehicle_type_2 || '-'}</td>
                          <td className="p-2 text-right font-mono">{tr.age_minutes}m</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PickupSlaCard() {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pinging, setPinging] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await api.get('/admin/pickup-sla');
        if (!cancelled) setData(res.data);
      } catch { /* ignore */ }
    };
    fetch();
    const t = setInterval(fetch, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handlePing = async (tripId) => {
    setPinging(p => ({ ...p, [tripId]: 'sending' }));
    try {
      const { data: res } = await api.post(`/admin/pickup-sla/ping/${tripId}`);
      const ok = res.whatsapp_status === 'sent' || res.whatsapp_status === 'no_api_key';
      setPinging(p => ({ ...p, [tripId]: ok ? 'sent' : 'failed' }));
      setTimeout(() => setPinging(p => { const n = { ...p }; delete n[tripId]; return n; }), 4000);
    } catch {
      setPinging(p => ({ ...p, [tripId]: 'failed' }));
      setTimeout(() => setPinging(p => { const n = { ...p }; delete n[tripId]; return n; }), 4000);
    }
  };

  if (!data) return null;
  const { summary, trips } = data;

  const META = {
    ok:     { label: 'On Time',  color: 'text-[#00C853]', bg: 'bg-[#00C853]/10', dot: 'bg-[#00C853]', hint: '< 30 min wait' },
    warn:   { label: 'Warning',  color: 'text-[#FF8C00]', bg: 'bg-[#FF8C00]/10', dot: 'bg-[#FF8C00]', hint: '30–60 min wait' },
    breach: { label: 'Breach',   color: 'text-[#FF3333]', bg: 'bg-[#FF3333]/10', dot: 'bg-[#FF3333]', hint: '> 60 min wait' },
  };

  return (
    <div data-testid="pickup-sla-card" className="bg-white border border-[#E5E5E5] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Hourglass size={18} className="text-[#8E44AD]" weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">Pickup SLA · Awaiting DC</h3>
        </div>
        <button
          onClick={() => setDialogOpen(true)}
          className="text-xs uppercase tracking-widest text-[#002FA7] hover:underline"
          data-testid="pickup-sla-view-all"
          disabled={summary.total === 0}
        >
          View all ({summary.total})
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {['ok', 'warn', 'breach'].map(b => {
          const meta = META[b];
          const count = summary[b] || 0;
          return (
            <button
              key={b}
              onClick={() => setDialogOpen(true)}
              className={`${meta.bg} border border-[#E5E5E5] p-3 text-left hover:shadow-md transition-shadow ${b === 'breach' && count > 0 ? 'animate-pulse' : ''}`}
              data-testid={`pickup-sla-${b}`}
              disabled={count === 0}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                <p className="text-[10px] uppercase tracking-widest text-[#666]">{meta.label}</p>
              </div>
              <p className={`text-2xl font-black mt-1 ${meta.color}`}>{count}</p>
              <p className="text-[10px] text-[#666] mt-0.5">{meta.hint}</p>
            </button>
          );
        })}
      </div>

      {summary.breach > 0 && (
        <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 px-3 py-2 flex items-start gap-2">
          <Warning size={14} className="text-[#FF3333] mt-0.5 shrink-0" weight="bold" />
          <p className="text-xs text-[#FF3333]">
            <strong>{summary.breach}</strong> trip{summary.breach === 1 ? '' : 's'} waiting over 60 minutes for DC. Tap the card to nudge the Pickup user.
          </p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Pickup SLA — {summary.total} trip{summary.total === 1 ? '' : 's'} awaiting DC</DialogTitle>
          </DialogHeader>
          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {trips.length === 0 ? (
              <p className="text-sm text-[#666] text-center py-8">No trips awaiting DC.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F3F3]">
                    <TableHead className="text-xs uppercase tracking-widest">Trip</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Supplier / Route</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Pickup User</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Age</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trips.map((t, i) => {
                    const meta = META[t.bucket];
                    const p = pinging[t.trip_id];
                    return (
                      <TableRow key={t.trip_id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`pickup-sla-row-${t.bucket}`}>
                        <TableCell>
                          <p className="font-mono text-xs">{t.trip_id?.slice(0, 8)}</p>
                          {t.priority && t.priority !== 'others' && <span className="text-[9px] uppercase tracking-widest bg-[#FF3333]/10 text-[#FF3333] px-1.5 py-0.5">{t.priority}</span>}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-sm">{t.pickup_supplier || '-'}</p>
                          <p className="text-[11px] text-[#666]">{t.pickup_location || '-'} → {t.drop_location || '-'}</p>
                          <p className="text-[10px] text-[#666]">{t.item} {t.qty}{t.uom}</p>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-sm">{t.pickup_user_name || '-'}</p>
                          {t.pickup_user_mobile ? <a href={`tel:${t.pickup_user_mobile}`} className="text-xs text-[#002FA7] hover:underline">{t.pickup_user_mobile}</a> : <span className="text-xs text-[#666]">-</span>}
                        </TableCell>
                        <TableCell>
                          <span className={`font-bold text-sm ${meta.color}`}>
                            {t.age_minutes < 60 ? `${t.age_minutes}m` : `${Math.floor(t.age_minutes / 60)}h ${t.age_minutes % 60}m`}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1 ${meta.bg} ${meta.color}`}>
                            <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {t.pickup_user_mobile ? (
                            <button
                              onClick={() => handlePing(t.trip_id)}
                              disabled={p === 'sending'}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold transition-colors ${
                                p === 'sent' ? 'bg-[#00C853]/10 text-[#00C853]'
                                : p === 'failed' ? 'bg-[#FF3333]/10 text-[#FF3333]'
                                : p === 'sending' ? 'bg-[#808080]/10 text-[#808080] cursor-not-allowed'
                                : 'bg-[#8E44AD] text-white hover:bg-[#8E44AD]/90'
                              }`}
                              data-testid={`pickup-sla-ping-${t.trip_id}`}
                              title="WhatsApp nudge the Pickup user to attach DC"
                            >
                              {p === 'sent' ? (<><CheckCircle size={14} weight="bold" /> Sent</>)
                                : p === 'failed' ? (<><Warning size={14} weight="bold" /> Failed</>)
                                : p === 'sending' ? 'Sending…'
                                : (<><PaperPlaneTilt size={14} weight="bold" /> Ping</>)}
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#999]">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LoadmanLiveCard() {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState('halt');  // 'halt' | 'type' | 'pilot'

  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await api.get('/admin/loadman-live');
        if (!cancelled) setData(res.data);
      } catch { /* ignore */ }
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return null;
  const { summary, by_halt, by_type, by_pilot } = data;

  return (
    <div data-testid="loadman-live-card" className="bg-white border border-[#E5E5E5] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-[#002FA7]" weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">Live Helpers · In-Flight Pickups</h3>
        </div>
        <button
          onClick={() => { setTab('halt'); setDialogOpen(true); }}
          className="text-xs uppercase tracking-widest text-[#002FA7] hover:underline"
          data-testid="loadman-live-view-all"
          disabled={summary.total_unique_loadmen === 0}
        >
          Details →
        </button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Unique Helpers</p>
          <p className="text-2xl font-black text-[#002FA7] mt-1" data-testid="loadman-live-total">{summary.total_unique_loadmen}</p>
          <p className="text-[10px] text-[#666] mt-0.5">Moving goods now</p>
        </div>
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Own</p>
          <p className="text-2xl font-black text-[#0A0A0A] mt-1">{by_type.own || 0}</p>
          <p className="text-[10px] text-[#666] mt-0.5">Company staff</p>
        </div>
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Contract</p>
          <p className="text-2xl font-black text-[#0A0A0A] mt-1">{by_type.contract || 0}</p>
          <p className="text-[10px] text-[#666] mt-0.5">Daily hired</p>
        </div>
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Active Trips</p>
          <p className="text-2xl font-black text-[#0A0A0A] mt-1">{summary.active_trips}</p>
          <p className="text-[10px] text-[#666] mt-0.5">Pilots carrying helpers</p>
        </div>
      </div>

      {/* Top halts inline preview */}
      {by_halt.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="loadman-live-halts-inline">
          {by_halt.slice(0, 6).map(h => (
            <button
              key={h.halt}
              onClick={() => { setTab('halt'); setDialogOpen(true); }}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7]/20"
            >
              <MapPin size={12} weight="bold" />
              <span className="font-medium">{h.halt}</span>
              <span className="font-bold">{h.count}</span>
            </button>
          ))}
          {by_halt.length > 6 && (
            <button onClick={() => { setTab('halt'); setDialogOpen(true); }} className="text-xs text-[#666] hover:underline px-2 py-1">
              +{by_halt.length - 6} more
            </button>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Live Helpers — {summary.total_unique_loadmen} on {summary.active_trips} in-flight pickups</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 border-b border-[#E5E5E5] mt-3">
            {[
              { key: 'halt', label: 'By Halt' },
              { key: 'type', label: 'By Type' },
              { key: 'pilot', label: 'By Pilot' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === t.key ? 'border-[#002FA7] text-[#002FA7] font-bold' : 'border-transparent text-[#666] hover:text-[#0A0A0A]'}`}
                data-testid={`loadman-live-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {tab === 'halt' && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F3F3]">
                    <TableHead className="text-xs uppercase tracking-widest">Halt Location</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Helpers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {by_halt.length === 0 ? (
                    <TableRow><TableCell colSpan={2} className="text-center text-[#666] py-6">No active helpers</TableCell></TableRow>
                  ) : by_halt.map((h, i) => (
                    <TableRow key={h.halt} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                      <TableCell className="font-medium">{h.halt}</TableCell>
                      <TableCell className="text-right font-bold text-[#002FA7]">{h.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === 'type' && (
              <div className="grid grid-cols-3 gap-4 p-4">
                {['own', 'contract', 'other'].map(k => (
                  <div key={k} className="bg-[#F3F3F3] border border-[#E5E5E5] p-4 text-center">
                    <p className="text-xs uppercase tracking-widest text-[#666]">{k === 'other' ? 'Other/Unknown' : k}</p>
                    <p className="text-3xl font-black text-[#0A0A0A] mt-2">{by_type[k] || 0}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === 'pilot' && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F3F3]">
                    <TableHead className="text-xs uppercase tracking-widest">Pilot · Vehicle</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Route</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Helpers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {by_pilot.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center text-[#666] py-6">No active pilots with helpers</TableCell></TableRow>
                  ) : by_pilot.map((p, i) => (
                    <TableRow key={p.trip_id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`loadman-live-pilot-row`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UserCircle size={18} className="text-[#002FA7]" />
                          <div>
                            <p className="font-medium text-sm">{p.pilot_name || p.pilot_id}</p>
                            <p className="text-[10px] font-mono text-[#666]">{p.vehicle_number || '-'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs">{p.pickup_location || '-'}</p>
                        <p className="text-xs text-[#666]">→ {p.drop_location || '-'}</p>
                        <p className="text-[10px] uppercase tracking-widest text-[#FF8C00] mt-0.5">{p.trip_status}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.loadmen.map(ld => (
                            <span key={ld.id || ld.name} className={`text-[10px] px-2 py-0.5 ${ld.type === 'own' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#FF8C00]/10 text-[#FF8C00]'}`}>
                              {ld.name}{ld.emp_id ? ` (${ld.emp_id})` : ''}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GoogleTimingMonitorCard() {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await api.get('/admin/google-timings/summary?days=1');
        if (!cancelled) setData(res.data);
      } catch { /* ignore */ }
    };
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return null;
  const { total_trips, trips_with_google, drift_count, drift_threshold_min, alerts } = data;
  const tone = drift_count === 0 ? 'ok' : drift_count >= 5 ? 'breach' : 'warn';
  const headerClass = tone === 'ok'
    ? 'bg-[#00C853]/5 border-[#00C853]/20'
    : tone === 'warn'
    ? 'bg-[#FF8C00]/5 border-[#FF8C00]/20'
    : 'bg-[#FF3333]/5 border-[#FF3333]/20';
  const accent = tone === 'ok' ? 'text-[#00C853]' : tone === 'warn' ? 'text-[#FF8C00]' : 'text-[#FF3333]';

  return (
    <div data-testid="google-timing-monitor-card" className={`border ${headerClass} p-4 space-y-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={20} className={accent} weight="bold" />
          <h3 className={`text-sm font-bold uppercase tracking-widest ${accent}`}>Google Timing Monitor</h3>
        </div>
        {alerts.length > 0 && (
          <button
            onClick={() => setDialogOpen(true)}
            className="text-xs uppercase tracking-widest underline text-[#666] hover:text-[#0A0A0A]"
            data-testid="google-timing-view-all"
          >
            View all
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Trips today</p>
          <p className="text-2xl font-black text-[#0A0A0A]" data-testid="google-timing-total">{total_trips}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#666]">GPS-tracked</p>
          <p className="text-2xl font-black text-[#002FA7]" data-testid="google-timing-tracked">{trips_with_google}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Drift &gt; {drift_threshold_min}m</p>
          <p className={`text-2xl font-black ${accent}`} data-testid="google-timing-drift-count">{drift_count}</p>
        </div>
      </div>

      {drift_count === 0 ? (
        <p className="text-xs text-[#666]">Pilot clicks match Google GPS within {drift_threshold_min} min — no manipulation flagged.</p>
      ) : (
        <div className="space-y-1.5">
          {alerts.slice(0, 5).map(a => (
            <div
              key={a.trip_id}
              className="flex items-center justify-between border border-[#E5E5E5] bg-white p-2"
              data-testid={`google-timing-alert-${a.trip_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate">
                  #{(a.trip_id || '').slice(0, 8)} · {a.pilot_name} · {a.vehicle_number}
                </p>
                <p className="text-[11px] text-[#666] truncate">{a.pickup} → {a.drop}</p>
              </div>
              <span className={`text-xs uppercase tracking-widest px-2 py-1 ${accent} bg-white border ${tone === 'breach' ? 'border-[#FF3333]/30' : 'border-[#FF8C00]/30'} font-bold ml-2`}>
                {a.drift_label} · {a.drift_min}m drift
              </span>
            </div>
          ))}
          {alerts.length > 5 && (
            <p className="text-[11px] text-[#666] text-center">+{alerts.length - 5} more — tap "View all"</p>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Google Timing Drift — Last 24 h</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#666] mt-1">
            Trips where the pilot's manual click time differs from the GPS-derived time by more than {drift_threshold_min} minutes.
            High drift can indicate the pilot tapped the button much earlier or later than they actually arrived/left.
          </p>
          <Table className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>Trip</TableHead>
                <TableHead>Pilot</TableHead>
                <TableHead>Route</TableHead>
                <TableHead className="text-right">Drift</TableHead>
                <TableHead className="text-right">Pickup wait</TableHead>
                <TableHead className="text-right">Drop wait</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map(a => (
                <TableRow key={a.trip_id}>
                  <TableCell className="font-mono">#{(a.trip_id || '').slice(0, 8)}</TableCell>
                  <TableCell>
                    <div className="font-bold">{a.pilot_name}</div>
                    <div className="text-[11px] text-[#666]">{a.vehicle_number}</div>
                  </TableCell>
                  <TableCell className="text-xs">{a.pickup} → {a.drop}</TableCell>
                  <TableCell className={`text-right font-bold ${accent}`}>{a.drift_label} · {a.drift_min}m</TableCell>
                  <TableCell className="text-right">{a.google_pickup_waiting_min != null ? `${a.google_pickup_waiting_min}m` : '-'}</TableCell>
                  <TableCell className="text-right">{a.google_drop_waiting_min != null ? `${a.google_drop_waiting_min}m` : '-'}</TableCell>
                  <TableCell className="text-right">{a.google_total_min != null ? `${Math.round(a.google_total_min)}m` : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GpsHealthCard() {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [pinging, setPinging] = useState({});   // {pilot_id: 'sending' | 'sent' | 'failed'}

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await api.get('/admin/gps-health');
        if (!cancelled) setData(res.data);
      } catch { /* ignore */ }
    };
    fetchHealth();
    const t = setInterval(fetchHealth, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handlePing = async (pilotId) => {
    setPinging(p => ({ ...p, [pilotId]: 'sending' }));
    try {
      const { data: res } = await api.post(`/admin/gps-health/ping/${pilotId}`);
      const ok = res.whatsapp_status === 'sent' || res.whatsapp_status === 'no_api_key';
      setPinging(p => ({ ...p, [pilotId]: ok ? 'sent' : 'failed' }));
      // Reset state after 4 s
      setTimeout(() => setPinging(p => { const n = { ...p }; delete n[pilotId]; return n; }), 4000);
    } catch {
      setPinging(p => ({ ...p, [pilotId]: 'failed' }));
      setTimeout(() => setPinging(p => { const n = { ...p }; delete n[pilotId]; return n; }), 4000);
    }
  };

  if (!data) return null;
  const { summary, pilots } = data;
  const visible = filter === 'all' ? pilots : pilots.filter(p => p.health === filter);

  return (
    <div data-testid="gps-health-card" className="bg-white border border-[#E5E5E5] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WifiHigh size={18} className="text-[#002FA7]" weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">GPS Health · Checked-In Pilots</h3>
        </div>
        <div className="flex items-center gap-2">
          {summary.offline > 0 && (
            <button
              onClick={async () => {
                const offlinePilots = pilots.filter(p => p.health === 'offline' && p.mobile);
                for (const p of offlinePilots) { await handlePing(p.pilot_id); }
              }}
              className="text-xs uppercase tracking-widest text-white bg-[#FF3333] hover:bg-[#FF3333]/90 px-3 py-1.5 flex items-center gap-1.5"
              data-testid="gps-health-ping-all-offline"
              title="Send WhatsApp nudge to every offline pilot"
            >
              <PaperPlaneTilt size={13} weight="bold" /> Ping all offline
            </button>
          )}
          <button
            onClick={() => { setFilter('all'); setDialogOpen(true); }}
            className="text-xs uppercase tracking-widest text-[#002FA7] hover:underline"
            data-testid="gps-health-view-all"
          >
            View all ({summary.total_checked_in})
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {['live', 'stale', 'offline'].map(h => {
          const meta = HEALTH_META[h];
          const count = summary[h] || 0;
          return (
            <button
              key={h}
              onClick={() => { setFilter(h); setDialogOpen(true); }}
              className={`${meta.bg} border border-[#E5E5E5] p-3 text-left hover:shadow-md transition-shadow`}
              data-testid={`gps-health-${h}`}
            >
              <div className="flex items-center gap-2">
                <meta.Icon size={16} className={meta.color} weight="bold" />
                <p className="text-[10px] uppercase tracking-widest text-[#666]">{meta.label}</p>
              </div>
              <p className={`text-2xl font-black mt-1 ${meta.color}`}>{count}</p>
              <p className="text-[10px] text-[#666] mt-0.5">
                {h === 'live' && 'Ping ≤ 60 s'}
                {h === 'stale' && 'Ping 1–5 min'}
                {h === 'offline' && 'Ping > 5 min / none'}
              </p>
            </button>
          );
        })}
      </div>

      {summary.offline > 0 && (
        <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 px-3 py-2 flex items-start gap-2">
          <Warning size={14} className="text-[#FF3333] mt-0.5 shrink-0" weight="bold" />
          <p className="text-xs text-[#FF3333]">
            <strong>{summary.offline}</strong> checked-in {summary.offline === 1 ? 'pilot has' : 'pilots have'} stopped sending GPS. Tap the card to contact them.
          </p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              GPS Health — {filter === 'all' ? 'All Checked-In Pilots' : HEALTH_META[filter].label}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {visible.length === 0 ? (
              <p className="text-sm text-[#666] text-center py-8">No pilots in this bucket.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F3F3]">
                    <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Halt</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Last GPS</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((p, i) => {
                    const meta = HEALTH_META[p.health];
                    const pingState = pinging[p.pilot_id];
                    const canPing = p.health !== 'live' && !!p.mobile;
                    return (
                      <TableRow key={p.pilot_id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`gps-health-row-${p.health}`}>
                        <TableCell className="font-medium">{p.name || p.pilot_id}</TableCell>
                        <TableCell>
                          {p.mobile ? (
                            <a href={`tel:${p.mobile}`} className="text-[#002FA7] hover:underline">{p.mobile}</a>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{p.halt_location || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{p.vehicle_number || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-xs">{fmtAgo(p.seconds_ago)}</span>
                            {p.last_gps_time && <span className="text-[10px] text-[#666]">{fmtTime(p.last_gps_time)}</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1 ${meta.bg} ${meta.color}`}>
                            <span className={`w-2 h-2 rounded-full ${meta.dot} ${p.health === 'live' ? 'animate-pulse-dot' : ''}`} />
                            {meta.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {canPing ? (
                            <button
                              onClick={() => handlePing(p.pilot_id)}
                              disabled={pingState === 'sending'}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold transition-colors ${
                                pingState === 'sent'
                                  ? 'bg-[#00C853]/10 text-[#00C853]'
                                  : pingState === 'failed'
                                  ? 'bg-[#FF3333]/10 text-[#FF3333]'
                                  : pingState === 'sending'
                                  ? 'bg-[#808080]/10 text-[#808080] cursor-not-allowed'
                                  : 'bg-[#002FA7] text-white hover:bg-[#002FA7]/90'
                              }`}
                              data-testid={`gps-ping-btn-${p.pilot_id}`}
                              title="Send WhatsApp nudge to reopen the Carloo app"
                            >
                              {pingState === 'sent' ? (
                                <><CheckCircle size={14} weight="bold" /> Sent</>
                              ) : pingState === 'failed' ? (
                                <><Warning size={14} weight="bold" /> Failed</>
                              ) : pingState === 'sending' ? (
                                <>Sending…</>
                              ) : (
                                <><PaperPlaneTilt size={14} weight="bold" /> Ping</>
                              )}
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#999]">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailDialog({ open, onClose, metric }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !metric) return;
    setLoading(true);
    api.get('/admin/dashboard/detail', { params: { metric } })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, metric]);

  const records = data?.records || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[800px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{data?.title || 'Details'}</DialogTitle>
          <p className="text-xs text-[#666]">{records.length} records</p>
        </DialogHeader>
        <div className="overflow-auto flex-1 mt-2">
          {loading ? (
            <div className="text-center py-8 text-[#666] text-sm">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center py-8 text-[#666] text-sm">No records found</div>
          ) : data?.type === 'pilots' ? (
            <Table>
              <TableHeader><TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Emp ID</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={r.id || i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.emp_id || '-'}</TableCell>
                    <TableCell>{r.mobile || '-'}</TableCell>
                    <TableCell>
                      <span className={`text-xs uppercase tracking-widest px-2 py-1 ${r.status === 'active' ? 'bg-[#00C853]/10 text-[#00C853]' : r.status === 'inactive' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#808080]/10 text-[#808080]'}`}>
                        {r.status || '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : data?.type === 'vehicles' ? (
            <Table>
              <TableHeader><TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-xs uppercase tracking-widest">Vehicle No.</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Body Type</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={r.id || i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                    <TableCell className="font-medium">{r.vehicle_number}</TableCell>
                    <TableCell>{r.body_type || '-'}</TableCell>
                    <TableCell>
                      <span className={`text-xs uppercase tracking-widest px-2 py-1 ${r.status === 'available' || r.status === 'active' ? 'bg-[#00C853]/10 text-[#00C853]' : r.status === 'in_use' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#FF3333]/10 text-[#FF3333]'}`}>
                        {r.status || '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : data?.type === 'trips' ? (
            <Table>
              <TableHeader><TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Pickup</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Drop</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={r.id || i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                    <TableCell className="font-mono text-xs">{r.id?.slice(0, 8)}</TableCell>
                    <TableCell>{r.pickup_supplier_name || '-'}</TableCell>
                    <TableCell>{r.drop_supplier_name || '-'}</TableCell>
                    <TableCell>{r.pilot_name || r.booked_by_name || '-'}</TableCell>
                    <TableCell>{r.vehicle_number || '-'}</TableCell>
                    <TableCell>
                      <span className={`text-xs uppercase tracking-widest px-2 py-1 ${STATUS_BADGE[r.status] || ''}`}>{r.status || r.priority || '-'}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : data?.type === 'pilot_trips' ? (
            <Table>
              <TableHeader><TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Trip</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.trip}</TableCell>
                    <TableCell>{r.vehicle || '-'}</TableCell>
                    <TableCell>
                      <span className={`text-xs uppercase tracking-widest px-2 py-1 ${STATUS_BADGE[r.trip_status] || ''}`}>{r.trip_status}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : data?.type === 'pilot_attendance' ? (
            <Table>
              <TableHeader><TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Check In</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Current Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{fmtTime(r.check_in)}</TableCell>
                    <TableCell><span className="text-xs uppercase tracking-widest">{r.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailMetric, setDetailMetric] = useState(null);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-[#666] text-sm">Loading dashboard...</div></div>;
  if (!dashboard) return <div className="text-[#FF3333] p-4">Failed to load dashboard</div>;

  const stats = [
    { label: 'Total Pilots', value: dashboard.total_pilots, icon: Users, color: 'border-[#002FA7]', metric: 'total_pilots' },
    { label: 'Total Vehicles', value: dashboard.total_vehicles, icon: Truck, color: 'border-[#00C853]', metric: 'total_vehicles' },
    { label: 'Active Trips', value: dashboard.active_trips, icon: MapPin, color: 'border-[#FFD700]', metric: 'active_trips' },
    { label: 'Pending Trips', value: dashboard.pending_trips, icon: Clock, color: 'border-[#FF8C00]', metric: 'pending_trips' },
    { label: 'Completed Today', value: dashboard.completed_trips, icon: ArrowUp, color: 'border-[#00C853]', metric: 'completed_trips' },
    { label: 'Trips Today', value: dashboard.total_trips_today, icon: ArrowDown, color: 'border-[#002FA7]', metric: 'total_trips_today' },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <DetailDialog open={!!detailMetric} onClose={() => setDetailMetric(null)} metric={detailMetric} />

      <div>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Dashboard</h1>
        <p className="text-[#666] text-sm mt-1">Real-time fleet overview — click any card for details</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map(s => (
          <div
            key={s.label}
            onClick={() => setDetailMetric(s.metric)}
            className={`bg-white border border-[#E5E5E5] border-l-4 ${s.color} p-4 cursor-pointer hover:shadow-md transition-shadow`}
            data-testid={`stat-card-${s.metric}`}
          >
            <div className="flex items-center justify-between">
              <s.icon size={20} className="text-[#666]" />
            </div>
            <p className="text-3xl font-black mt-2 text-[#0A0A0A]">{s.value}</p>
            <p className="text-xs uppercase tracking-widest text-[#666] mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Pilot Status Grid */}
      <div>
        <h2 className="text-xl font-bold tracking-tight text-[#0A0A0A] mb-4">Pilot Status Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(dashboard.pilot_statuses || {}).map(([key, count]) => {
            const colors = STATUS_COLORS[key] || STATUS_COLORS.nonproductive;
            return (
              <div
                key={key}
                onClick={() => setDetailMetric(`pilot_status_${key}`)}
                className={`${colors.bg} border border-[#E5E5E5] p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow`}
                data-testid={`pilot-status-${key}`}
              >
                <div className={`w-3 h-3 rounded-full ${colors.dot} ${count > 0 ? 'animate-pulse-dot' : ''}`} />
                <div>
                  <p className={`text-2xl font-black ${colors.text}`}>{count}</p>
                  <p className="text-xs uppercase tracking-widest text-[#666]">{STATUS_LABELS[key] || key}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Queue Health — pending trips grouped by vehicle type */}
      <QueueHealthCard />

      {/* Pickup SLA — trips awaiting DC confirmation */}
      <PickupSlaCard />

      {/* Live Loadmen — on in-flight pickup trips */}
      <LoadmanLiveCard />

      {/* Phase 76k — Google Timing Monitor (manual click vs GPS-derived) */}
      <GoogleTimingMonitorCard />

      {/* GPS Offline Health — who checked in but stopped sending live GPS */}
      <GpsHealthCard />

      {/* Alerts */}
      {(dashboard.excess_pickups > 0 || dashboard.excess_drops > 0) && (
        <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={20} className="text-[#FF3333]" weight="bold" />
            <h3 className="text-sm font-bold uppercase tracking-widest text-[#FF3333]">Alerts</h3>
          </div>
          <div className="flex gap-6 text-sm">
            {dashboard.excess_pickups > 0 && (
              <span className="text-[#0A0A0A]">Excess Pickup Waiting: <strong className="text-[#FF3333]">{dashboard.excess_pickups}</strong></span>
            )}
            {dashboard.excess_drops > 0 && (
              <span className="text-[#0A0A0A]">Excess Drop Waiting: <strong className="text-[#FF3333]">{dashboard.excess_drops}</strong></span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
